import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { meQuery } from '../api/jedi'
import { FLAGS } from '../flags/registry'

export const Route = createFileRoute('/_shell/flags')({
  component: FlagsGate,
})

/**
 * The route gate — and it is COSMETIC, recorded as cosmetic rather than
 * described as a boundary (ADR-0040).
 *
 * The registry it guards is `../flags/registry`, which is compiled into the
 * browser bundle and shipped to everyone who loads the app. Anyone who wants
 * its contents can read them out of the JavaScript without ever visiting this
 * route. So this hides a page; it conceals nothing.
 *
 * That is still worth doing — an internal instrument should not sit in a
 * signed-in user's way as though it were theirs — but calling it a boundary
 * would be the exact failure this wave exists to retire: a check whose only
 * evidence is a hidden UI route. The real boundary in step 4 is on
 * `/eval-reports`, at the API, where the data actually lives.
 */
function FlagsGate() {
  const me = useQuery(meQuery)
  if (me.isPending) return null
  if (!me.data?.instruments) {
    return (
      <section className="surface">
        <header className="surface-head">
          <h1>Feature flags</h1>
          <p className="surface-sub">
            This page is for the people who run the platform. Ask one of them if you need
            something from it.
          </p>
        </header>
      </section>
    )
  }
  return <FlagsPage />
}

/**
 * The flag registry, readable (not linked from the rail — a dev/ops URL).
 * An entry is a ledger record, not a work order; release notes generate from
 * the registry diff between tags.
 */
function FlagsPage() {
  const scheduled = Object.values(FLAGS).filter((f) => f.disposition === 'approved')
  const designAhead = Object.values(FLAGS).filter((f) => f.disposition === 'not-scheduled')

  return (
    <section className="surface">
      <header className="surface-head">
        <h1>Feature flags</h1>
        <p className="surface-sub">Registry defaults determine production behaviour.</p>
      </header>
      <h2 className="surface-h2">Approved for phase 1</h2>
      <ul className="flag-list">
        {scheduled.map((f) => (
          <li key={f.key}>
            <code>{f.key}</code>: {f.description}
          </li>
        ))}
      </ul>
      <h2 className="surface-h2">Designed ahead, not scheduled</h2>
      <ul className="flag-list">
        {designAhead.map((f) => (
          <li key={f.key}>
            <code>{f.key}</code>: {f.description}
          </li>
        ))}
      </ul>
    </section>
  )
}
